import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Bot,
  CalendarClock,
  CheckCircle2,
  Clock3,
  PhoneCall,
  Sparkles,
} from "lucide-react";
import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55 },
};

const APP_SURFACES = [
  {
    icon: Bot,
    title: "An advisor that sees the work",
    body: "The AI advisor reads the operational context around customers, orders, appointments, and staff activity so its suggestions are grounded in what is actually happening.",
  },
  {
    icon: PhoneCall,
    title: "A phone host for the routine calls",
    body: "The AI phone host can answer common questions, capture structured intent, and hand off the moments that need a human instead of treating every call the same.",
  },
  {
    icon: CalendarClock,
    title: "Calendar and follow-up awareness",
    body: "Appointments, availability, customer messages, and next steps stay visible to the assistant so the system can help with scheduling, reminders, and handoff.",
  },
];

const BUSINESS_TYPES = [
  "Restaurant",
  "Cafe or bakery",
  "Retail shop",
  "Service business",
  "Dispensary",
  "Other",
];

function WaitlistForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_type: "",
    ai_consent: false,
    company: "",
  });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!form.email.trim()) {
      toast.error("Email is required.");
      return;
    }
    if (!form.ai_consent) {
      toast.error("Please acknowledge the AI disclosure before joining.");
      return;
    }

    setSending(true);
    try {
      const { data } = await api.post("/waitlist", form);
      setResult(data);
      toast.success("You are on the app waitlist.");
    } catch (err) {
      const code = err?.response?.status;
      toast.error(
        code === 429
          ? "Too many attempts. Please try again in a little while."
          : err?.response?.data?.detail || "Could not join the waitlist. Please try again."
      );
    } finally {
      setSending(false);
    }
  };

  if (result) {
    return (
      <Card className="p-7 lg:p-8">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div>
            <p className="overline mb-2">you are on the list</p>
            <h2 className="text-2xl font-bold mb-2">We will email early access details.</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If you are on the sign-up list before launch, we will email a unique one-time-use
              promo code for one free month of Ordrs. The code is for waitlist members only, and
              can be applied when you are ready to activate or continue your subscription.
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3">
          <Button asChild variant="outline" className="font-bold">
            <Link to="/demo">Explore the demo</Link>
          </Button>
          <Button asChild variant="ghost" className="font-bold">
            <Link to="/pricing">See current pricing</Link>
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-7 lg:p-8">
      <form onSubmit={submit} className="space-y-5">
        <input
          type="text"
          name="company"
          value={form.company}
          onChange={set("company")}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="waitlist-name">Name</Label>
            <Input id="waitlist-name" value={form.name} onChange={set("name")} placeholder="Your name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="waitlist-email">Email</Label>
            <Input id="waitlist-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@business.com" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="waitlist-business-type">Business type</Label>
          <select
            id="waitlist-business-type"
            value={form.business_type}
            onChange={set("business_type")}
            className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">Select one</option>
            {BUSINESS_TYPES.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <label className="flex items-start gap-3 rounded-lg border border-border p-4 text-sm leading-relaxed">
          <Checkbox
            checked={form.ai_consent}
            onCheckedChange={(checked) => setForm((current) => ({ ...current, ai_consent: checked === true }))}
            className="mt-0.5"
          />
          <span>
            I understand Ordrs uses AI to advise, answer calls, draft replies, read operational context, and support staff.
            I want early access, product updates, and the launch offer by email.
          </span>
        </label>

        <Button type="submit" size="lg" disabled={sending} className="h-12 w-full text-base font-bold group">
          {sending ? "Joining..." : (
            <>
              Join the waitlist
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground">
          Join before launch to receive the one-time-use promo code for one free month of Ordrs.
        </p>
      </form>
    </Card>
  );
}

export default function Waitlist() {
  return (
    <div>
      <RevealNav />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="/build%20samples/ordrsAI%20(2).png"
            alt=""
            className="h-full w-full object-cover opacity-[0.08]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/90 to-background" />
        </div>
        <div className="ds-container pt-32 pb-16 lg:pt-40 lg:pb-24">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <motion.div className="lg:col-span-7" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="overline mb-6">ordrs app waitlist</p>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl mb-6 max-w-4xl">
                The AI assistant for operators who need the work to stay visible.
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
                Ordrs is building around the assistant first: an AI advisor that can see what is
                happening, an AI phone host for routine calls, calendar awareness, messages, and
                staff handoff. Join the sign-up list before launch and we will email a unique
                one-time-use promo code for one free month of Ordrs.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Early app access</span>
                <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> One free month promo code</span>
                <span className="inline-flex items-center gap-2"><Clock3 className="h-4 w-4 text-primary" /> Must join before launch</span>
              </div>
            </motion.div>

            <motion.div className="lg:col-span-5" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}>
              <WaitlistForm />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="ds-container py-16 lg:py-24">
        <div className="max-w-3xl mb-12">
          <motion.p className="overline mb-3" {...fade}>what you can inspect now</motion.p>
          <motion.h2 className="text-3xl lg:text-4xl mb-4" {...fade}>
            This is not a pitch deck. It is the assistant layer moving closer to the floor.
          </motion.h2>
          <motion.p className="text-muted-foreground leading-relaxed" {...fade}>
            The current product already has the pieces the assistant needs: the advisor surface,
            AI phone flows, customer context, calendar paths, storefront activity, and staff operations.
            The waitlist tells us who wants that help closer to the daily workflow.
          </motion.p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {APP_SURFACES.map((surface, index) => (
            <motion.div key={surface.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.45, delay: index * 0.08 }}>
              <Card className="h-full p-6">
                <surface.icon className="mb-4 h-5 w-5 text-primary" />
                <h3 className="mb-2 text-lg font-bold">{surface.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{surface.body}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-card/40">
        <div className="ds-container grid gap-8 py-16 lg:grid-cols-12 lg:items-center">
          <div className="lg:col-span-7">
            <p className="overline mb-3">explore while you are here</p>
            <h2 className="text-3xl lg:text-4xl mb-3">The waitlist should not feel like a dead end.</h2>
            <p className="text-muted-foreground leading-relaxed">
              Outreach can send people here without forcing a sales call. They can join, see what Ordrs already does,
              and decide whether an AI assistant belongs in their daily workflow before we email the launch promo.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:col-span-5 lg:justify-end">
            <Button asChild className="font-bold">
              <Link to="/demo">Open demo</Link>
            </Button>
            <Button asChild variant="outline" className="font-bold">
              <Link to="/solutions">See workflows</Link>
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
